import React from "react";
import { Modal, Descriptions, Tag, Table, Space, Button } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { RoomPackage } from "../../../types";

const CHARGE_BASIS_LABELS: Record<string, string> = {
  per_room_per_night: "Per Room/Night",
  per_person_per_night: "Per Person/Night",
  per_stay: "Per Stay",
  per_person_per_stay: "Per Person/Stay",
  complimentary: "Complimentary",
};

interface RoomPackageDetailModalProps {
  open: boolean;
  pkg: RoomPackage | null;
  canManage: boolean;
  onCancel: () => void;
  onEdit: () => void;
  onDelete: (id: string) => void;
}

const RoomPackageDetailModal: React.FC<RoomPackageDetailModalProps> = ({
  open,
  pkg,
  canManage,
  onCancel,
  onEdit,
  onDelete,
}) => {
  if (!pkg) return null;

  const inclusionColumns = [
    { title: "Type", dataIndex: "item_type", key: "item_type", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Name", dataIndex: "item_name", key: "item_name" },
    {
      title: "Qty",
      key: "qty",
      width: 100,
      render: (_: any, record: any) =>
        record.is_unlimited ? (
          <span style={{ color: "#1890ff", fontWeight: 500 }}>Unlimited</span>
        ) : (
          record.quantity
        ),
    },
  ];

  return (
    <Modal
      title={`Package: ${pkg.name}`}
      open={open}
      onCancel={onCancel}
      width={640}
      footer={
        canManage ? (
          <Space>
            <Button onClick={onCancel}>Close</Button>
            <Button icon={<EditOutlined />} onClick={onEdit}>
              Edit
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(pkg.id)}>
              Delete
            </Button>
          </Space>
        ) : (
          <Button onClick={onCancel}>Close</Button>
        )
      }
    >
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="Code">
          <span style={{ fontFamily: "monospace" }}>{pkg.code}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Category">{pkg.category || "-"}</Descriptions.Item>
        <Descriptions.Item label="Charge Basis">{CHARGE_BASIS_LABELS[pkg.charge_basis] || pkg.charge_basis}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={pkg.is_active ? "green" : "default"}>{pkg.is_active ? "Active" : "Inactive"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Base Price">
          LKR {Number(pkg.base_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </Descriptions.Item>
        <Descriptions.Item label="Price/Person">
          LKR {Number(pkg.price_per_person).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </Descriptions.Item>
        <Descriptions.Item label="Sort Order">{pkg.sort_order}</Descriptions.Item>
        <Descriptions.Item label="Description" span={2}>
          {pkg.description || "-"}
        </Descriptions.Item>
      </Descriptions>

      {pkg.inclusions && pkg.inclusions.length > 0 && (
        <>
          <h4 style={{ marginTop: 16, marginBottom: 8 }}>Inclusions</h4>
          <Table
            rowKey="id"
            columns={inclusionColumns}
            dataSource={pkg.inclusions}
            pagination={false}
            size="small"
          />
        </>
      )}
    </Modal>
  );
};

export default RoomPackageDetailModal;
